/**
 * Readable system snapshot with technical details available on demand.
 * Missing metrics remain unavailable rather than being presented as failures.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress } from '@material-ui/core';
import {
  CancelOutlined,
  CheckCircle,
  Error as ErrorIcon,
  FileCopyOutlined,
  Storage,
  Speed,
  Update,
  Warning as WarningIcon,
} from '@material-ui/icons';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';
import AppLocale from '../../../../lngProvider';
import styles from './DiagnosticsSnapshotPanel.module.scss';
import IntlMessages from 'util/IntlMessages';

const PLUGIN_CAPABILITIES = Object.freeze([
  ['operatorStateV1', 'operator.diag.capability.searchHealth'],
  ['indexProgressV1', 'operator.diag.capability.indexingProgress'],
  ['onboardingCheckpointsV1', 'operator.diag.capability.setupCheckpoints'],
  ['catalogSelectorsV1', 'operator.diag.capability.productSelectionRules'],
  ['availabilityRulesV1', 'operator.diag.capability.availabilityRules'],
  ['payloadProvidersV1', 'operator.diag.capability.productDataPayload'],
  ['variantPresentationSettingsV1', 'operator.diag.capability.variationPresentation'],
  ['variantGroupingV1', 'operator.diag.capability.variantGrouping'],
  ['diagnosticEventsV1', 'operator.diag.capability.diagnosticEvents'],
  ['diagnosticSnapshotV1', 'operator.diag.capability.systemSnapshot'],
  ['publishStateV1', 'operator.diag.capability.publishStatus'],
  ['liveEnabledV1', 'operator.diag.capability.liveSearchControl'],
]);

const SNAPSHOT_FIELDS = Object.freeze([
  ['operator.diag.system.pluginVersion', 'pluginVersion'],
  ['operator.diag.system.coreVersion', 'coreVersion'],
  ['operator.diag.system.adminAppVersion', 'adminAppVersion'],
  ['operator.diag.system.schemaVersion', 'schemaVersion'],
  ['operator.diag.system.activeIndexPresent', 'activeIndexPresent'],
  ['operator.diag.system.activeIndexRecords', 'activeIndexRecords'],
  ['operator.diag.system.passiveIndexRecords', 'passiveIndexRecords'],
  ['operator.diag.system.activeIndexSizeBytes', 'activeIndexSizeBytes'],
  ['operator.diag.system.passiveIndexSizeBytes', 'passiveIndexSizeBytes'],
  ['operator.diag.system.lastIndexingStatus', 'lastIndexingStatus'],
  ['operator.diag.system.lastIndexingStarted', 'lastIndexingStartedAt'],
  ['operator.diag.system.lastIndexingFinished', 'lastIndexingFinishedAt'],
  ['operator.diag.system.lastIndexingDurationMs', 'lastIndexingDurationMs'],
  ['operator.diag.system.lastIndexingProductsPerSecond', 'lastIndexingProductsPerSecond'],
  ['operator.diag.system.lastIndexingErrorCode', 'lastIndexingErrorCode'],
  ['operator.diag.system.phpVersion', 'phpVersion'],
  ['operator.diag.system.phpMemoryLimit', 'phpMemoryLimit'],
  ['operator.diag.system.databaseServer', 'databaseServerVersion'],
  ['operator.diag.system.databaseNameHash', 'databaseNameHash'],
  ['operator.diag.system.serverSoftware', 'serverSoftware'],
]);

const fallbackLocalize = (id, values) => {
  const template = AppLocale.en?.messages?.[id] || id;
  return Object.entries(values || {}).reduce(
    (message, [key, value]) => message.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value)),
    template,
  );
};

const copyValue = (value, t) => {
  if (value === null || value === undefined || value === '') return t('operator.diag.system.unavailable');
  return String(value);
};

export function buildSnapshotCopyText(snapshot, capabilities, pluginCapabilities, warnings, localizeMessage = fallbackLocalize) {
  const lines = [localizeMessage('operator.diag.system.copyTitle'), '', localizeMessage('operator.diag.system.information')];
  SNAPSHOT_FIELDS.forEach(([labelKey, key]) => lines.push(`${localizeMessage(labelKey)}: ${copyValue(snapshot?.[key], localizeMessage)}`));
  lines.push('', localizeMessage('operator.diag.system.snapshotCapabilities'));
  Object.entries(capabilities || {}).forEach(([key, value]) => {
    lines.push(`${key}: ${localizeMessage(value === true ? 'operator.diag.system.available' : 'operator.diag.system.unavailable')}`);
  });
  lines.push('', localizeMessage('operator.diag.system.pluginCompatibility'));
  PLUGIN_CAPABILITIES.forEach(([key, labelKey]) => {
    lines.push(`${localizeMessage(labelKey)} (${key}): ${localizeMessage(pluginCapabilities?.[key] === true ? 'operator.diag.system.supportedInReport' : 'operator.diag.system.notAdvertised')}`);
  });
  if (warnings?.length) {
    lines.push('', localizeMessage('operator.diag.system.warnings'));
    warnings.forEach((warning) => lines.push(JSON.stringify(warning)));
  }
  return lines.join('\n');
}

async function writeClipboard(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Cross-origin admin iframes may expose the Clipboard API while denying
      // permission. The user gesture can still use the legacy copy command.
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);
  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!copied) throw new Error('Clipboard copy failed');
}

export function formatDuration(ms) {
  if (ms === null || ms === undefined) return null;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.round(ms / 60000)} min`;
}

export function formatBytes(bytes) {
  if (bytes === null || bytes === undefined) return null;
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const order = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / (1024 ** order)).toFixed(order === 0 ? 0 : 1)} ${units[order]}`;
}

export function formatDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function displayNumber(value) {
  return value === null || value === undefined ? null : Number(value).toLocaleString();
}

function MetricRow({ label, value, statusClass }) {
  const unavailable = value === null || value === undefined;
  return (
    <div className={styles.metricRow}>
      <span className={styles.metricLabel}>{label}</span>
      {unavailable
        ? <span className={styles.metricUnavailable}><IntlMessages id="ui.home.diagnosticsSnapshotPanel.unavailable" /></span>
        : <span className={`${styles.metricValue} ${statusClass || ''}`}>{String(value)}</span>}
    </div>
  );
}

MetricRow.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  statusClass: PropTypes.string,
};
MetricRow.defaultProps = { value: null, statusClass: null };

function Section({ title, children, className }) {
  return (
    <div className={`${styles.section} ${className || ''}`}>
      <p className={styles.sectionTitle}>{title}</p>
      {children}
    </div>
  );
}
Section.propTypes = {
  title: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};
Section.defaultProps = { className: null };

function statusClass(status) {
  if (status === 'completed') return styles.statusOk;
  if (status === 'failed') return styles.statusError;
  if (status === 'running') return styles.statusRunning;
  return null;
}

function statusIcon(status) {
  if (status === 'completed') return <CheckCircle />;
  if (status === 'failed') return <ErrorIcon />;
  return <Update />;
}

function HeadlineMetric({ icon, label, value, hint, tone }) {
  return (
    <div className={`${styles.headlineMetric} ${tone ? styles[tone] : ''}`}>
      <span className={styles.headlineIcon}>{icon}</span>
      <span className={styles.headlineLabel}>{label}</span>
      <strong>{value === null || value === undefined ? '—' : value}</strong>
      <small>{hint}</small>
    </div>
  );
}

HeadlineMetric.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hint: PropTypes.node.isRequired,
  tone: PropTypes.string,
};
HeadlineMetric.defaultProps = { value: null, tone: null };

export default function DiagnosticsSnapshotPanel({
  snapshot,
  capabilities,
  pluginCapabilities,
  warnings,
  isLoading,
  error,
}) {
  const localizeMessage = useLocalizedMessage();
  const [copyState, setCopyState] = useState('idle');

  if (isLoading) {
    return (
      <section className={styles.panel} data-testid="snapshot-panel">
        <div className={styles.stateContainer} data-testid="snapshot-loading"><CircularProgress size={24} /></div>
      </section>
    );
  }
  if (error) {
    return (
      <section className={styles.panel} data-testid="snapshot-panel">
        <div className={`${styles.stateContainer} ${styles.errorState}`} data-testid="snapshot-error">
          <ErrorIcon />
          <strong><IntlMessages id="ui.home.diagnosticsSnapshotPanel.systemDetailsCouldNotBeLoaded" /></strong>
          <p>{error}</p>
        </div>
      </section>
    );
  }
  if (!snapshot) return null;

  const duration = formatDuration(snapshot.lastIndexingDurationMs);
  const records = displayNumber(snapshot.activeIndexRecords);
  const throughput = snapshot.lastIndexingProductsPerSecond === null
    || snapshot.lastIndexingProductsPerSecond === undefined
    ? null
    : `${displayNumber(snapshot.lastIndexingProductsPerSecond)}/sec`;
  const handleCopyDetails = async () => {
    try {
      await writeClipboard(buildSnapshotCopyText(
        snapshot,
        capabilities,
        pluginCapabilities,
        warnings,
        localizeMessage,
      ));
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
  };

  return (
    <section className={styles.panel} data-testid="snapshot-panel">
      <div className={styles.panelHeader}>
        <div>
          <p className={styles.eyebrow}><IntlMessages id="ui.home.diagnosticsSnapshotPanel.runtimeCompatibility" /></p>
          <h3 className={styles.panelTitle}><IntlMessages id="ui.home.diagnosticsSnapshotPanel.systemInformation" /></h3>
          <p className={styles.panelSubtitle}><IntlMessages id="ui.home.diagnosticsSnapshotPanel.versionsAndTechnicalDetailsForTroubleshootingOrSupport" /></p>
        </div>
        <div className={styles.panelActions}>
          <span className={`${styles.runStatus} ${statusClass(snapshot.lastIndexingStatus) || ''}`}>
            {statusIcon(snapshot.lastIndexingStatus)}
            {snapshot.lastIndexingStatus
              ? localizeMessage('operator.diag.status.value', { status: snapshot.lastIndexingStatus.replace(/_/g, ' ') })
              : localizeMessage('operator.diag.system.statusUnavailable')}
          </span>
          <Button
            variant="outlined"
            size="small"
            className={styles.copyButton}
            startIcon={copyState === 'copied' ? <CheckCircle /> : <FileCopyOutlined />}
            onClick={handleCopyDetails}
            data-testid="copy-system-details"
          >
            {localizeMessage(copyState === 'copied'
              ? 'operator.diag.system.copied'
              : copyState === 'error'
                ? 'operator.diag.system.copyFailed'
                : 'operator.diag.system.copy')}
          </Button>
        </div>
      </div>

      {warnings.length > 0 && (
        <div className={styles.warningNotice} data-testid="snapshot-warnings">
          <WarningIcon />
          <span>
            {localizeMessage('operator.diag.system.metricsUnavailable', { count: warnings.length })}
          </span>
        </div>
      )}

      <div className={styles.headlineGrid}>
        <HeadlineMetric
          icon={<Storage />}
          label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.searchableRecords" />}
          value={records}
          hint={localizeMessage(snapshot.activeIndexPresent ? 'operator.diag.system.activeIndex' : 'operator.diag.system.noActiveIndex')}
          tone={snapshot.activeIndexPresent ? 'metricMint' : 'metricMuted'}
        />
        <HeadlineMetric
          icon={<Update />}
          label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.lastProcessingRun" />}
          value={duration}
          hint={formatDate(snapshot.lastIndexingFinishedAt) || localizeMessage('operator.diag.system.notCompletedYet')}
          tone="metricBlue"
        />
        <HeadlineMetric
          icon={<Speed />}
          label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.processingSpeed" />}
          value={throughput}
          hint={localizeMessage('operator.diag.system.productsIndexed')}
          tone="metricViolet"
        />
      </div>

      <details className={styles.technicalDetails} open>
        <summary>
          <span>
            <strong><IntlMessages id="ui.home.diagnosticsSnapshotPanel.versionsIndexesAndEnvironment" /></strong>
            <small><IntlMessages id="ui.home.diagnosticsSnapshotPanel.useTheseDetailsForTroubleshootingOrWhenContactingSupport" /></small>
          </span>
        </summary>
        <div className={styles.detailsGrid}>
          <Section title={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.versions" />}>
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.pluginVersion" />} value={snapshot.pluginVersion} />
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.coreVersion" />} value={snapshot.coreVersion} />
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.adminAppVersion" />} value={snapshot.adminAppVersion} />
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.schemaVersion" />} value={snapshot.schemaVersion} />
          </Section>
          <Section title={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.indexStorage" />}>
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.activeIndex" />} value={localizeMessage(snapshot.activeIndexPresent ? 'operator.diag.system.present' : 'operator.diag.system.notPresent')} statusClass={snapshot.activeIndexPresent ? styles.statusOk : null} />
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.activeRecords" />} value={records} />
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.passiveRecords" />} value={displayNumber(snapshot.passiveIndexRecords)} />
            {capabilities.indexSizeBytes && (
              <>
                <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.activeIndexSize" />} value={formatBytes(snapshot.activeIndexSizeBytes)} />
                <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.passiveIndexSize" />} value={formatBytes(snapshot.passiveIndexSizeBytes)} />
              </>
            )}
          </Section>
          <Section title={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.lastProcessingRun" />}>
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.status" />} value={snapshot.lastIndexingStatus} statusClass={statusClass(snapshot.lastIndexingStatus)} />
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.started" />} value={formatDate(snapshot.lastIndexingStartedAt)} />
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.finished" />} value={formatDate(snapshot.lastIndexingFinishedAt)} />
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.duration" />} value={duration} />
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.productsSec" />} value={displayNumber(snapshot.lastIndexingProductsPerSecond)} />
            {snapshot.lastIndexingStatus === 'failed' && <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.errorCode" />} value={snapshot.lastIndexingErrorCode} statusClass={styles.statusError} />}
          </Section>
          <Section title={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.environment" />}>
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.phpVersion" />} value={snapshot.phpVersion} />
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.phpMemoryLimit" />} value={snapshot.phpMemoryLimit} />
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.databaseServer" />} value={snapshot.databaseServerVersion} />
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.databaseNameHash" />} value={snapshot.databaseNameHash} />
            <MetricRow label={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.serverSoftware" />} value={snapshot.serverSoftware} />
          </Section>
          {Object.keys(pluginCapabilities).length > 0 && (
            <Section title={<IntlMessages id="ui.home.diagnosticsSnapshotPanel.pluginCompatibility" />} className={styles.compatibilitySection}>
              <p className={styles.compatibilityHint}>
                <IntlMessages id="ui.home.diagnosticsSnapshotPanel.featuresExplicitlyAdvertisedByTheInstalledPluginTheseAreCompatibility" />
              </p>
              <div className={styles.compatibilityGrid} data-testid="snapshot-plugin-capabilities">
                {PLUGIN_CAPABILITIES.map(([key, labelKey]) => {
                  const supported = pluginCapabilities[key] === true;
                  return (
                    <div className={styles.capabilityItem} key={key} data-supported={supported}>
                      {supported ? <CheckCircle /> : <CancelOutlined />}
                      <span>
                        <strong>{localizeMessage(labelKey)}</strong>
                        <small>{localizeMessage(supported ? 'operator.diag.system.supported' : 'operator.diag.system.notAdvertisedByPlugin')}</small>
                      </span>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}
        </div>
      </details>
    </section>
  );
}

DiagnosticsSnapshotPanel.propTypes = {
  snapshot: PropTypes.shape({
    pluginVersion: PropTypes.string,
    coreVersion: PropTypes.string,
    adminAppVersion: PropTypes.string,
    schemaVersion: PropTypes.string,
    activeIndexPresent: PropTypes.bool,
    activeIndexRecords: PropTypes.number,
    passiveIndexRecords: PropTypes.number,
    activeIndexSizeBytes: PropTypes.number,
    passiveIndexSizeBytes: PropTypes.number,
    lastIndexingStartedAt: PropTypes.string,
    lastIndexingFinishedAt: PropTypes.string,
    lastIndexingDurationMs: PropTypes.number,
    lastIndexingProductsPerSecond: PropTypes.number,
    lastIndexingStatus: PropTypes.string,
    lastIndexingErrorCode: PropTypes.string,
    phpVersion: PropTypes.string,
    phpMemoryLimit: PropTypes.string,
    databaseServerVersion: PropTypes.string,
    databaseNameHash: PropTypes.string,
    serverSoftware: PropTypes.string,
  }),
  capabilities: PropTypes.shape({ indexSizeBytes: PropTypes.bool, latencySummary: PropTypes.bool }),
  pluginCapabilities: PropTypes.objectOf(PropTypes.bool),
  warnings: PropTypes.arrayOf(PropTypes.shape({ metric: PropTypes.string, code: PropTypes.string })),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
};

DiagnosticsSnapshotPanel.defaultProps = {
  snapshot: null,
  capabilities: { indexSizeBytes: false, latencySummary: false },
  pluginCapabilities: {},
  warnings: [],
  isLoading: false,
  error: null,
};
